import { TranslateModule } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';

/** Meddig marad a „másolva" visszajelzés a gombon. */
const COPIED_FEEDBACK_MS = 1500;

/**
 * A foglaláshoz tartozó mobilappos belépő shortcode (pl. „buho47") megjelenítése másolás-gombbal.
 *
 * `badge`: a foglalás-adatlap gyorsinfó-sávjának színes jelölője.
 * `field`: csak olvasható űrlapmező a „Hivatkozások" blokkba — a foglalási kód mezőjének párja.
 */
@Component({
  selector: 'app-login-token',
  standalone: true,
  imports: [TranslateModule, InputTextModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (token(); as value) {
      @if (variant() === 'field') {
        <div class="relative flex items-center">
          <input
            pInputText
            type="text"
            readonly
            [value]="value"
            [attr.aria-label]="'booking.loginToken.label' | translate"
            class="w-full bg-white pr-12 font-mono tracking-wider"
          />
          <button
            type="button"
            class="absolute right-3 top-1/2 -translate-y-1/2 transform rounded-full bg-gray-100 p-1 transition-colors duration-200 hover:bg-gray-200"
            [pTooltip]="(copied() ? 'common.copied' : 'common.copyToClipboard') | translate"
            tooltipPosition="top"
            (click)="copy(value)"
          >
            <i
              class="text-xs"
              [class]="copied() ? 'pi pi-check text-green-600' : 'pi pi-copy text-gray-500'"
            ></i>
          </button>
        </div>
      } @else {
        <span class="align-items-center border-round shadow-1 inline-flex bg-teal-100 py-[8px] pl-3 pr-1 text-sm text-teal-900">
          <i class="pi pi-mobile mr-2 leading-[14px]"></i>
          <strong class="leading-[14px]">{{ 'booking.loginToken.short' | translate }}</strong>
          <span class="ml-2 font-mono font-semibold leading-[14px] tracking-wider">{{ value }}</span>
          <button
            type="button"
            class="ml-2 rounded-full px-1 transition-colors duration-200 hover:bg-teal-200"
            [pTooltip]="(copied() ? 'common.copied' : 'common.copyToClipboard') | translate"
            tooltipPosition="bottom"
            (click)="copy(value)"
          >
            <i
              class="text-xs leading-[14px]"
              [class]="copied() ? 'pi pi-check' : 'pi pi-copy'"
            ></i>
          </button>
        </span>
      }
    }
  `
})
export class LoginTokenComponent {
  readonly token = input<string | null | undefined>(null);
  readonly variant = input<'badge' | 'field'>('badge');
  readonly copied = signal(false);
  private feedbackTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.feedbackTimer));
  }

  copy(value: string): void {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value);
    this.copied.set(true);
    clearTimeout(this.feedbackTimer);
    this.feedbackTimer = setTimeout(() => this.copied.set(false), COPIED_FEEDBACK_MS);
  }
}
